import { Component, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { BinaryDownload, CatalogProduct, CatalogProductDependency, ProductBinary, ProductComponent } from 'src/app/model/CatalogProduct';
import { UserInfo } from 'src/app/model/User';
import { FileSizePipe } from 'src/app/pipe/file-size.pipe';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { CatalogService } from 'src/app/service/catalog.service';
import { DataTableService } from 'src/app/service/data-table.service';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { CatalogComponent } from '../catalog/catalog.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableFilterOption, DataTableRow, ErrorColumnType } from '../controls/datatable/datatable.component';
import { Modal } from '../controls/modal/modal.service';
import { ConfirmOverwriteComponent, ConfirmOverwriteModalData } from '../dialogs/confirm-overwrite/confirm-overwrite.component';
import { CookieService } from 'ngx-cookie-service';
import { Events, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ProductDataType } from '../products/products.component';
import { DownloadItem } from '../products-overview/products-overview.component';
import { DownloadType } from 'src/app/model/DownloadStatus';

@Component({
    selector: 'app-catalog-download',
    templateUrl: './catalog-download.component.html',
    styleUrls: ['./catalog-download.component.css'],
    standalone: false
})
export class CatalogDownloadComponent extends CatalogComponent implements OnInit {

  cols: DataTableColumn[];
  rows: DataTableRow[] = [];
  downloaded: DataTableRow[] = [];
  productsToDownload: DataTableRow[] = [];
  product: CatalogProduct;
  downloadInfo: CatalogProduct[];
  productName: string;
  version: string;
  osLoaded: boolean = false;
  operatingSystems: CatalogProductDependency[];
  selectedOperatingSystem: CatalogProductDependency;
  totalSize: number = 0;
  totalSizeStr: string;
  totalSelected: number = 0;
  totalErrors: number = 0;
  totalErrorsStr: string;
  viewHeight: string = "340";

  isOnlineMode: boolean = false;
  isProxyRequired: boolean = false;

  itemsToDownload: DownloadItem;
  showDownloadStarted: boolean = false;

  solutionInfoSubscription: Subscription;
  routeSubscription: Subscription;
  dataSubscription: Subscription;
  dataServiceSubscription: Subscription;

  constructor(public authSrv: AuthenticationService, public catalogSrv: CatalogService, public eventService: EventService, public router: Router,
    public dataSrv: DataService, public route: ActivatedRoute, private dataTableSrv: DataTableService,
    public softwareSrv: SoftwareInventoryService, public wsSrv: WebsocketService, private cookieSrv: CookieService, public modal: Modal, public el: ElementRef) {

    super(catalogSrv, eventService, router, dataSrv, route, modal, el);
    this.isOnlineMode = this.authSrv.getLoggedInUser().settings.isOnlineMode;
    this.cols = this.isOnlineMode ? Array<DataTableColumn>(
      new DataTableColumn('productName', 'Name', ColumnType.STRING, true, "20", ColumnSort.ASC),
      new DataTableColumn('componentName', 'Component', ColumnType.STRING, false, "20"),
      new DataTableColumn('binaryName', 'Binary', ColumnType.STRING, false, "20"),
      new DataTableColumn('version', 'Version', ColumnType.STRING, false, "10"),
      new DataTableColumn('os', 'Operating System', ColumnType.STRING, false, "20"),
      new DataTableColumn('size', 'Size', ColumnType.FILESIZE, false, "10")) :
      Array<DataTableColumn>(
        new DataTableColumn('productName', 'Name', ColumnType.STRING, true, "20", ColumnSort.ASC),
        new DataTableColumn('componentName', 'Component', ColumnType.STRING, false, "20"),
        new DataTableColumn('binaryName', 'Binary', ColumnType.STRING, false, "20"),
        new DataTableColumn('version', 'Version', ColumnType.STRING, false, "10"),
        new DataTableColumn('os', 'Operating System', ColumnType.STRING, false, "18"),
        new DataTableColumn('size', 'Size', ColumnType.FILESIZE, false, "6"),
        new DataTableColumn('url', 'URL', ColumnType.DOWNLOADLINK, false, "6"));
      this.isLoading = false;
  }

  ngOnInit(): void {

    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.isManifestUpdating = newSolutionInfo.manifestStatus.isUpdating;
      this.isSapMode = newSolutionInfo.isSModeEnabled;
      if (this.routeSubscription == undefined) {
        this.initView();
      }
    });
  }

  initView(): void {
    this.routeSubscription = this.route.parent.params.subscribe(params => {
      this.productName = params.product;
      this.version = params.version;
      this.getProductVersion();
    });

    this.dataSubscription = this.dataTableSrv.valueSelected$.subscribe(data => {
      this.updateRowValue(data.value, data.row);
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if (msg.eventType == Events.ManifestUpdateStarted) {
        this.isManifestUpdating = true;
      } else if (msg.eventType == Events.ManifestUpdateComplete) {
        this.reloadAfterManifestUpdate();
      }
    });

    this.dataServiceSubscription = this.dataSrv.getData().subscribe((data: any) => {
      if (data && data.type == ProductDataType.DownloadStarted && this.showDownloadStarted) {
        this.showDownloadStarted = false;
        Utils.showDownloadStartedDialog(DownloadType.PRODUCT, this.itemsToDownload.getTotalItems(), this.modal, this.cookieSrv);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.solutionInfoSubscription != undefined) {
      this.solutionInfoSubscription.unsubscribe();
    }
    if (this.routeSubscription != undefined) {
      this.routeSubscription.unsubscribe();
    }
    if (this.dataSubscription != undefined) {
      this.dataSubscription.unsubscribe();
    }
    if (this.eventMessageSubscription != undefined) {
      this.eventMessageSubscription.unsubscribe();
    }
    if(this.dataServiceSubscription != undefined) {
      this.dataServiceSubscription.unsubscribe();
    }
  }

  onSelectAll(event: any): void {
    this.calculateTotals(this.rows);
  }

  onDownload(): void {
    if(this.selectedOperatingSystem != undefined){
      this.validateDownloadInfo(this.selectedOperatingSystem.uid);
    } else {
      this.validateDownloadRequest();
    }
  }

  getProductVersion(): void {
    this.catalogSrv.getProductVersion(decodeURIComponent(this.productName)).pipe(
      tap(resp => {
        resp.body.forEach((prod) => {
          if (prod.product_uid === this.version) {
            this.product = prod;
            this.dataSrv.sendData(this.product);
            this.getOperatingSystems();
            return;
          }
        });
      })
    ).subscribe();
  }

  getOperatingSystems(): void {
    this.catalogSrv.getDependencies(this.version).pipe(
      tap(resp => {
        this.operatingSystems = [];
        let tmpDep = resp.body.dependencies["Operating System"];
        if(tmpDep != undefined){
          let appKeys = Object.keys(tmpDep);
          for(let i = 0; i < appKeys.length; i++){
            let deps = tmpDep[appKeys[i]];
            for(let j = 0; j < deps.length; j++){
              let depsFound: CatalogProductDependency[] = this.operatingSystems.filter((dep:CatalogProductDependency) => {
                return dep.displayName == deps[j].displayName;
              });
              if(depsFound == undefined || depsFound.length == 0){
                this.operatingSystems.push(deps[j]);
              }
            }
          }
        }

        this.osLoaded = true;
        if (this.operatingSystems == undefined || this.operatingSystems.length == 0) {
          this.getDownloadInfo("independent");
        }
      })
    ).subscribe();
  }

  showOSDropDown(): boolean {
    var show = true;
    if (this.osLoaded) {
      if (this.operatingSystems == undefined || this.operatingSystems.length == 0) {
        show = false;
      }
    }else{
      show = false;
    }
    
    return show;
  }

  getDownloadInfo(selectedOSUid: string): void {
    this.isLoading = true;
    var filters: DataTableFilterOption[];
    this.catalogSrv.getDownloadInfo(this.product.bundle, selectedOSUid, this.version).pipe(
      tap(resp => {
        this.downloadInfo = resp.body;
        this.rows = [];
        var tmprows: DataTableRow[] = [];
        let count: number = this.getDownloadCount(this.downloadInfo);
        this.downloadInfo.forEach(product => {
          product.productComponents.productComponent.forEach(component => {
            filters = this.getOSSelectionList(component);

            if (component.productBinaries.productBinary.length > 1) {
              var selectedBinary: boolean = false;
              component.productBinaries.productBinary.forEach(binary => {
                binary.prerequisites.requiredProductVersion.forEach(dependency => {
                  if (selectedOSUid === dependency.uid) {
                    selectedBinary = true;
                    tmprows.push(this.getDownloadRow(count, product, component, (product.product_uid == this.version), selectedOSUid));
                    return;
                  }
                });
                if (!selectedBinary && filters.length == 1) {
                  selectedBinary = true;
                  tmprows.push(this.getDownloadRow(count, product, component, (product.product_uid == this.version), undefined));
                }
              });

              if (!selectedBinary) {
                tmprows.push(this.getDownloadRow(count, product, component, false, undefined));
              }
            } else {
              var binary = component.productBinaries.productBinary[0];
              if (this.isOsDependent(binary)) {
                var selectedBinary: boolean = false;
                binary.prerequisites.requiredProductVersion.forEach(dependency => {
                  if (selectedOSUid === dependency.uid) {
                    selectedBinary = true;
                    tmprows.push(this.getDownloadRow(count, product, component, (product.product_uid == this.version), selectedOSUid));
                    return;
                  }
                });

                if (!selectedBinary) {
                  if (filters.length == 1) {
                    if (!selectedBinary && filters.length == 1) {
                      selectedBinary = true;
                      tmprows.push(this.getDownloadRow(count, product, component, (product.product_uid == this.version), undefined));
                    }
                  } else {
                    tmprows.push(this.getDownloadRow(count, product, component, false, undefined));
                  }
                }
              } else {
                var tmpRow = new DataTableRow(new BinaryDownload(this.getProductName(product), product.product_uid, component.displayName, component.componentuid,
                  binary.displayName, binary.binaryuid, product.version, binary.size, 'N/A', binary.downloadURL, binary), null);
                if (product.product_uid == this.version) {
                  tmpRow.disableCheckbox = true;
                }
                tmpRow.selected = true;
                tmprows.push(tmpRow);
              }
            }
          });
          if (product.languagePackages != undefined) {
            product.languagePackages.forEach(langpack => {
              var cName = "Languages for " + product.productName + " " + product.version;
              var langBinary = new ProductBinary();
              langBinary.displayName = langpack.langName;
              langBinary.size = langpack.size;
              langBinary.binaryuid = langpack.binaryuid;
              langBinary.hashcode = langpack.hashcode;
              langBinary.downloadURL = langpack.downloadURL;
              langBinary.isDownloaded = langpack.isDownloaded;
              langBinary.isLang = true;
              langBinary.updateBinaryUID = langpack.updateBinaryUID;
              var tmpRow = new DataTableRow(new BinaryDownload(product.productName, product.product_uid, cName, langpack.binaryuid,
                langpack.langName, langpack.binaryuid, product.version, langpack.size, 'N/A', langBinary.downloadURL, langBinary), null);
              tmpRow.selected = true;
              tmprows.push(tmpRow);
            });
          }
        });

        this.rows = tmprows;
      }),
      finalize(() => {
        this.calculateTotals(this.rows);
        this.isLoading = false;
      })
    ).subscribe();
  }

  getDownloadCount(products: CatalogProduct[]): number{
    let count: number = 0;
    products.forEach(product => {
      product.productComponents.productComponent.forEach(component => {
        if (component.productBinaries.productBinary.length > 1) {
          count++;
        }
      });
    });
    return count;
  }

  isSupportedOs(binary: ProductBinary, os: string): boolean {
    var supported: boolean = false;
    binary.prerequisites.requiredProductVersion.forEach(reqversion => {
      if (reqversion.uid === os) {
        supported = true;
        return;
      }
    });
    return supported;
  }

  isOsDependent(binary: ProductBinary): boolean {
    var dependent: boolean = false;
    binary.prerequisites.requiredProductVersion.forEach(reqversion => {
      if (reqversion.category === 'Operating System') {
        dependent = true;
        return;
      }
    });
    return dependent;
  }

  getOSSelectionList(component: ProductComponent): DataTableFilterOption[] {
    var osList: DataTableFilterOption[] = [];
    component.productBinaries.productBinary.forEach(binary => {
      binary.prerequisites.requiredProductVersion.forEach(reqversion => {
        if (reqversion.category === 'Operating System') {
          osList.push(new DataTableFilterOption(reqversion.displayName, reqversion.uid));
          return;
        }
      });
    });
    return osList;
  }

  getBinarySelectionList(component: ProductComponent): DataTableFilterOption[] {
    var binaryList: DataTableFilterOption[] = [];
    component.productBinaries.productBinary.forEach(binary => {
      binaryList.push(new DataTableFilterOption(binary.displayName, binary.binaryuid));
    });
    return binaryList;
  }

  getFirstOS(component: ProductComponent): CatalogProductDependency {
    var os: CatalogProductDependency;
    component.productBinaries.productBinary.forEach(binary => {
      binary.prerequisites.requiredProductVersion.forEach(reqversion => {
        if (reqversion.category === 'Operating System') {
          os = reqversion;
          return;
        }
      });

      if (os != undefined) {
        return;
      }
    });
    return os;
  }

  onSelectOS(event: any): void {
    this.operatingSystems.forEach(os => {
      if (os.uid == event.target.value) {
        this.selectedOperatingSystem = os;
        return;
      }
    });
    this.getDownloadInfo(event.target.value);
  }

  getDownloadRow(count: number, product: CatalogProduct, component: ProductComponent, disableCheckbox: boolean,  selected: string): DataTableRow {
    var row: DataTableRow;
    var filters: DataTableFilterOption[] = this.getOSSelectionList(component);
    var tmpBinary = new BinaryDownload(product.productName, product.product_uid, component.displayName, component.componentuid,
      '', '', product.version, 0, '', '', undefined);

    row = new DataTableRow(tmpBinary, null);
    row.selected = true;
    row.disableCheckbox = disableCheckbox;
    
    if (filters.length == 1){
      row.error = false;
      this.updateRowValue(filters[0].value, row);
    } else if (filters.length > 1) {
      row.error = true;
      if(count > 1){
        row.errorField = 'os';
        row.errorColumnType = ErrorColumnType.DROPDOWN;
      }
      if(selected !== undefined){
        for(let i = 0; i < filters.length; i++){
          if (filters[i].value === selected){
            filters[i].selected = true;
            this.updateRowValue(filters[i].value, row);
            break;
          }
        }
      }
      row.errorValues = filters;
    } else {
      row.error = true;
      filters = this.getBinarySelectionList(component);
      tmpBinary.os = 'N/A';
      row.errorField = 'binaryName';
      row.errorColumnType = ErrorColumnType.DROPDOWN;
      row.errorValues = filters;
    }

    return row;
  }

  updateRowValue(value: string, updatedRow: DataTableRow): void {
    let selectedBinary: ProductBinary = this.getBinaryByOsForDownload(updatedRow.data.productuid, updatedRow.data.componentuid, value);

    if(selectedBinary == undefined){
      selectedBinary = this.getBinaryForDownload(updatedRow.data.productuid, updatedRow.data.componentuid, value);
    }

    if (updatedRow.errorField == "os" || updatedRow.data.os == "") {
      let osName = this.getSelectedOsName(updatedRow.data.productuid, updatedRow.data.componentuid, value);
      updatedRow.data.os = osName ? osName : '';
    }

    //binary info found
    if(selectedBinary){
      updatedRow.data.binary = selectedBinary;
      updatedRow.data.binaryName = selectedBinary.displayName;
      updatedRow.data.size = selectedBinary.size
      updatedRow.data.binaryuid = selectedBinary.binaryuid;
      updatedRow.error = false;
      updatedRow.data.url = selectedBinary.downloadURL;
    //binary info not found (user selecting empty value)
    }else{
      updatedRow.data.binary = undefined;
      updatedRow.data.binaryName = '';
      updatedRow.data.size = 0;
      updatedRow.data.binaryuid = '';
      updatedRow.error = true;
      updatedRow.data.url = '';
    }

    this.calculateTotals(this.rows);
  }

  getBinaryForDownload(productuid: string, componentuid: string, binaryuid: string): ProductBinary {
    var binaryToDownload: ProductBinary;
    this.downloadInfo.forEach(product => {
      if (product.product_uid === productuid) {
        product.productComponents.productComponent.forEach(component => {
          if (component.componentuid === componentuid) {
            component.productBinaries.productBinary.forEach(binary => {
              if (binary.binaryuid === binaryuid) {
                binaryToDownload = binary;
                return;
              }
            });

            if (binaryToDownload != undefined) {
              return;
            }
          }
        });

        if (binaryToDownload != undefined) {
          return;
        }
      }
    });

    return binaryToDownload;
  }

  getBinaryByOsForDownload(productuid: string, componentuid: string, osuid: string): ProductBinary {
    var binaryToDownload: ProductBinary;
    this.downloadInfo.forEach(product => {
      if (product.product_uid === productuid) {
        product.productComponents.productComponent.forEach(component => {
          if (component.componentuid === componentuid) {
            component.productBinaries.productBinary.forEach(binary => {
              binary.prerequisites.requiredProductVersion.forEach(reqversion => {
                if (reqversion.uid === osuid) {
                  binaryToDownload = binary;
                  return;
                }
              });

              if (binaryToDownload != undefined) {
                return;
              }
            });

            if (binaryToDownload != undefined) {
              return;
            }
          }
        });

        if (binaryToDownload != undefined) {
          return;
        }
      }
    });

    return binaryToDownload;
  }

  getSelectedOsName(productuid: string, componentuid: string, osuid: string): string {
    var selectedOS: string;
    this.downloadInfo.forEach(product => {
      if (product.product_uid === productuid) {
        product.productComponents.productComponent.forEach(component => {
          if (component.componentuid === componentuid) {
            component.productBinaries.productBinary.forEach(binary => {
              binary.prerequisites.requiredProductVersion.forEach(reqversion => {
                if (reqversion.uid === osuid) {
                  selectedOS = reqversion.displayName;
                  return;
                }
              });

              if (selectedOS != undefined) {
                return;
              }
            });

            if (selectedOS != undefined) {
              return;
            }
          }
        });

        if (selectedOS != undefined) {
          return;
        }
      }
    });

    return selectedOS;
  }

  isDownloadDisabled(): boolean {
    var isDownloadDisabled: boolean = false;
    var totalSelected: number = 0;
    if (this.rows !== undefined) {
      this.rows.forEach(row => {
        if (row.selected) {
          totalSelected++;
        }
      });
    }
    if (this.rows.length == 0 || this.totalErrors > 0 || totalSelected == 0) {
      isDownloadDisabled = true;
    }
    return isDownloadDisabled;
  }

  calculateTotals(data: DataTableRow[]): void {
    var totalSize: number = 0;
    var totalSelected: number = 0;
    var totalErrors: number = 0;
    var viewHeightBase: number = this.showOSDropDown() ? 340 : 190;

    if (data !== undefined) {
      data.forEach(row => {
        if (row.error && row.selected) {
          totalErrors++;
        }
        if (row.selected) {
          totalSelected++;
          totalSize += row.data.size;
        }
      });
    }

    viewHeightBase += (totalErrors > 0) ? 65 : 0;
    viewHeightBase += (totalSelected > 0) ? 135 : 25;

    this.viewHeight = viewHeightBase.toString();

    this.totalSize = totalSize;
    this.totalSizeStr = "The current selection requires at least " + new FileSizePipe().transform(totalSize) + " of disk space."
    this.totalSelected = totalSelected;
    this.totalErrors = totalErrors;
    this.totalErrorsStr = "There are " + totalErrors + " products/components which cannot be downloaded. The problems must be corrected before continuing.";
  }

  getDownloadedList(): DataTableRow[] {
    var downloaded: DataTableRow[] = [];
    this.rows.forEach(row => {
      if (row.selected && (row.data.binary.isDownloaded || row.data.binary.isDownloading)) {
        downloaded.push(row);
      }
    });

    return downloaded;
  }

  validateDownloadInfo(selectedOSUid: string): void {
    var filters: DataTableFilterOption[];
    this.catalogSrv.getDownloadInfo(this.product.bundle, selectedOSUid, this.version).pipe(
      tap(resp => {
        resp.body.forEach(product => {
          product.productComponents.productComponent.forEach(component => {
            filters = this.getOSSelectionList(component);

            if (component.productBinaries.productBinary.length > 1) {
              var selectedBinary: boolean = false;
              component.productBinaries.productBinary.forEach(binary => {
                binary.prerequisites.requiredProductVersion.forEach(dependency => {
                  if (selectedOSUid === dependency.uid) {
                    this.updateDownloadStatus(binary.binaryuid, binary.isDownloaded, binary.isDownloading, binary.downloadStatus);
                    return;
                  }
                });
                if (!selectedBinary && filters.length == 1) {
                  this.updateDownloadStatus(binary.binaryuid, binary.isDownloaded, binary.isDownloading, binary.downloadStatus);
                }
              });
            } else {
              var binary = component.productBinaries.productBinary[0];
              if (this.isOsDependent(binary)) {
                var selectedBinary: boolean = false;
                binary.prerequisites.requiredProductVersion.forEach(dependency => {
                  if (selectedOSUid === dependency.uid) {
                    this.updateDownloadStatus(binary.binaryuid, binary.isDownloaded, binary.isDownloading, binary.downloadStatus);
                    return;
                  }
                });

                if (!selectedBinary) {
                  if (filters.length == 1) {
                    if (!selectedBinary && filters.length == 1) {
                      this.updateDownloadStatus(binary.binaryuid, binary.isDownloaded, binary.isDownloading, binary.downloadStatus);
                    }
                  }
                }
              } else {
                this.updateDownloadStatus(binary.binaryuid, binary.isDownloaded, binary.isDownloading, binary.downloadStatus);
              }
            }

            if (product.languagePackages != undefined) {
              product.languagePackages.forEach(langpack => {
                this.updateDownloadStatus(langpack.binaryuid, langpack.isDownloaded, false, undefined);
              });
            }
          });
        });
      }),
      finalize(() => {
        this.validateDownloadRequest();
      })
    ).subscribe();
  }

  updateDownloadStatus(uid: string, isDownloaded: boolean, isDownloading: boolean, status: any): void{
    for(var i = 0; i < this.rows.length; i++){
      if(this.rows[i].data.binary.binaryuid == uid){
        this.rows[i].data.binary.downloadStatus = status;
        this.rows[i].data.binary.isDownloaded = isDownloaded;
        this.rows[i].data.binary.isDownloading = isDownloading;
        break;
      }
    }
  }

  validateDownloadRequest(): void {
    this.calculateTotals(this.rows);
    this.downloaded = this.getDownloadedList();
    if (this.downloaded.length > 0) {
      const sub: Subscription =
        this.modal.open(ConfirmOverwriteComponent, {
          data: new ConfirmOverwriteModalData(this.downloaded, (this.totalSelected == this.downloaded.length)),
          id: 'otsc-overwrite-download-dialog',
          maxheight: '800px'
        }
        ).afterClosed.subscribe((result) => {
          if (result) {
            this.productsToDownload = this.rows;
            this.download();
          } else {
            if (this.totalSelected != this.downloaded.length) {
              this.filterOverwrites();
            }
          }
          sub.unsubscribe();
        });
    } else {
      this.productsToDownload = this.rows;
      this.download();
    }
  }

  filterOverwrites(): void{
    this.productsToDownload = [];
    this.rows.forEach(row=>{
      if (row.selected && !row.data.binary.isDownloaded){
        this.productsToDownload.push(row);
      }
    });
    this.download();
  }

  download(): void {
    let productSummaryText: string = "";
    let user: UserInfo = this.authSrv.getLoggedInUser();
    this.itemsToDownload = new DownloadItem();
    this.isOnlineMode = user.settings.isOnlineMode;
    this.isProxyRequired = user.settings.proxyPrompt;
    this.showDownloadStarted = true;


    if (this.productsToDownload.length > 3) {
      productSummaryText = this.totalSelected + " components selected."
    } else {
      this.productsToDownload.forEach(row => {
        if (row.selected) {
          if (row.data.binary.isLang) {
            productSummaryText += row.data.componentName + ", ";
          } else {
            productSummaryText += row.data.productName + " " + row.data.version + ", ";
          }

        }
      });
      productSummaryText = productSummaryText.substring(0, productSummaryText.length - 2);
    }

    this.productsToDownload.forEach(row => {
      if (row.selected) {
        this.itemsToDownload.addDownloadedProduct(row.data.binary);
      }
    });

    this.calculateTotals(this.productsToDownload);
    Utils.startDownload(this.isProxyRequired, 0, "", this.itemsToDownload, productSummaryText, this.modal, this.dataSrv, this.softwareSrv);
  }
}
